import Mapping from "../mappings/Mapping";
import { AsanaNotificationInput } from "./AsanaNotificationInput";
import { EmailNotificationInput } from "./EmailNotificationInput";
import { FrontNotificationInput } from "./FrontNotificationInput";
import { HeightAppNotificationInput } from "./HeightAppNotificationInput";
import { HubSpotNotificationInput } from "./HubSpotNotificationInput";
import { IntercomNotificationInput } from "./IntercomNotificationInput";
import { JiraNotificationInput } from "./JiraNotificationInput";
import { LinearNotificationInput } from "./LinearNotificationInput";
import { NotionNotificationInput } from "./NotionNotificationInput";
import { SMSNotificationInput } from "./SMSNotificationInput";
import { SalesforceNotificationInput } from "./SalesforceNotificationInput";
import { SendbirdNotificationInput } from "./SendbirdNotificationInput";
import { SlackNotificationInput } from "./SlackNotificationInput";
import { TelegramNotificationInput } from "./TelegramNotificationInput";
import { TrelloNotificationInput } from "./TrelloNotificationInput";
import { WebhookNotificationInput } from "./WebhookNotificationInput";
import { WhatsAppNotificationInput } from "./WhatsAppNotificationInput";
import { ZendeskNotificationInput } from "./ZendeskNotificationInput";
import { getIntegrations, getNotificationInputChoices } from "./utils";
import { NotificationTypeMapToDefaultFilters, getTemplateForMappingType, isValidMapping, notificationTypeToMappingType } from "@avenueops/frontend-shared";
import { ClientMapping, ManualNotificationType } from "@avenueops/shared-types";
import { InputOption, SmartForm } from "@avenueops/web-components";
import { NotificationType, NotificationFilterType } from "@prisma/client";
import _ from "lodash";
import React, { useEffect, useMemo } from "react";
import { useFormContext, useWatch, Controller } from "react-hook-form";
import { trpc } from "utils/trpc";

interface Props {
  notificationPrefix: string;
  required?: boolean;
  disableCustomize?: boolean;
}

export default function NotificationInput({ notificationPrefix, required = true, disableCustomize }: Props) {
  const notification: ManualNotificationType = useWatch({ name: notificationPrefix });

  const manualType = useWatch({ name: `${notificationPrefix}.manualType` });

  const { control, watch, getFieldState, clearErrors, register, unregister, setValue, resetField, setError } = useFormContext();

  const selectedIntegrationId = watch(`${notificationPrefix}.integrationId`);
  const useMappingOverride = watch(`${notificationPrefix}.useMappingOverride`);

  const { data: integrations } = trpc.integrations.list.useQuery();
  const filteredIntegrations = useMemo(() => getIntegrations({ integrations, selectedIntegrationId }), [integrations, selectedIntegrationId]);
  const {
    slackIntegrations,
    sendgridIntegration,
    twilioIntegration,
    zendeskIntegrations,
    webhookIntegrations,
    notionIntegrations,
    intercomIntegrations,
    sendbirdIntegrations,
    asanaInstances,
    linearInstances,
    heightAppInstances,
    jiraInstances,
    trelloInstances,
    frontInstances,
    mergeIntegrationData,
    salesforceIntegrations,
    hubSpotIntegrations,
  } = filteredIntegrations;

  const filteredChoices: InputOption[] = useMemo(() => getNotificationInputChoices({ integrations: filteredIntegrations }), [filteredIntegrations]);

  useEffect(() => {
    // Mappings
    clearErrors(`${notificationPrefix}.recipientValue`);
    if (notificationTypeToMappingType(manualType)) {
      register(`${notificationPrefix}.mapping`, {
        required: true,
        value: getTemplateForMappingType(manualType),
      });
    } else {
      unregister(`${notificationPrefix}.mapping`);
    }
  }, [manualType]);

  const mappingFieldState = getFieldState(`${notificationPrefix}.mapping`);

  return (
    <div className={"w-full"}>
      <div className="w-full grid-cols-1 gap-y-1 gap-x-4 grid md:grid-cols-6">
        <div className="max-w-[300px] sm:col-span-2">
          <SmartForm.DropdownSelect
            onChangeCallback={(newType) => {
              resetField(`${notificationPrefix}.mapping`);
              resetField(`${notificationPrefix}.integrationId`);
              if (newType && notificationTypeToMappingType(newType as NotificationType)) {
                setValue(`${notificationPrefix}.mapping`, getTemplateForMappingType(newType as NotificationType));
              }
              setValue(
                `${notificationPrefix}.useMappingOverride`,
                newType === NotificationType.CUSTOMER_IO ||
                  newType === NotificationType.ZENDESK ||
                  newType === NotificationType.INTERCOM ||
                  newType === NotificationType.NOTION ||
                  newType === NotificationType.LINEAR ||
                  newType === NotificationType.HEIGHT ||
                  newType === NotificationType.SALESFORCE ||
                  newType === NotificationType.HUBSPOT,
              );
              resetField(`${notificationPrefix}.notificationFilters`);
              if (newType) {
                setValue(`${notificationPrefix}.notificationFilters`, NotificationTypeMapToDefaultFilters[newType as NotificationType]);
              }
            }}
            name={`${notificationPrefix}.manualType`}
            title="Notification Type"
            options={filteredChoices}
            disableUnselect={true}
          />
          {notification.manualType === NotificationType.ZENDESK ||
          notification.manualType === NotificationType.EMAIL ||
          notification.manualType === NotificationType.SLACK ? (
            <div className="mt-2">
              <SmartForm.Checkbox name={`${notificationPrefix}.includeAttachments`} label="Include CSV attachment of results" small />
            </div>
          ) : null}
          {notification.manualType === NotificationType.EMAIL && sendgridIntegration ? (
            <div className="mt-4">
              <SmartForm.Checkbox name={`${notificationPrefix}.useCustomIntegration`} label="Use custom Sendgrid" small defaultValue={true} />
            </div>
          ) : null}
          {notification.manualType === NotificationType.SMS && twilioIntegration ? (
            <div className="mt-4">
              <SmartForm.Checkbox name={`${notificationPrefix}.useCustomIntegration`} label="Use custom Twilio" small defaultValue={true} />
            </div>
          ) : null}
        </div>

        {notification.manualType === NotificationType.SMS ? (
          <SMSNotificationInput disableCustomize={disableCustomize} notificationPrefix={notificationPrefix} />
        ) : notification.manualType === NotificationType.WHATSAPP ? (
          <WhatsAppNotificationInput disableCustomize={disableCustomize} notificationPrefix={notificationPrefix} />
        ) : notification.manualType === NotificationType.SLACK ? (
          <SlackNotificationInput disableCustomize={disableCustomize} notificationPrefix={notificationPrefix} slackIntegrations={slackIntegrations} />
        ) : notification.manualType === NotificationType.NOTION && notionIntegrations && notionIntegrations.length > 0 ? (
          <NotionNotificationInput disableCustomize={disableCustomize} notificationPrefix={notificationPrefix} notionIntegrations={notionIntegrations} />
        ) : notification.manualType === NotificationType.WEBHOOK ? (
          <WebhookNotificationInput disableCustomize={disableCustomize} notificationPrefix={notificationPrefix} webhookIntegrations={webhookIntegrations} />
        ) : notification.manualType === NotificationType.TELEGRAM ? (
          <TelegramNotificationInput disableCustomize={disableCustomize} notificationPrefix={notificationPrefix} />
        ) : notification.manualType === NotificationType.ZENDESK && zendeskIntegrations && zendeskIntegrations.length > 0 ? (
          <ZendeskNotificationInput disableCustomize={disableCustomize} notificationPrefix={notificationPrefix} zendeskIntegrations={zendeskIntegrations} />
        ) : notification.manualType === NotificationType.SENDBIRD && sendbirdIntegrations && sendbirdIntegrations.length > 0 ? (
          <SendbirdNotificationInput disableCustomize={disableCustomize} notificationPrefix={notificationPrefix} />
        ) : notification.manualType === NotificationType.SALESFORCE && salesforceIntegrations && salesforceIntegrations.length > 0 ? (
          <SalesforceNotificationInput notificationPrefix={notificationPrefix} salesforceIntegrations={salesforceIntegrations} />
        ) : notification.manualType === NotificationType.HUBSPOT && hubSpotIntegrations && hubSpotIntegrations.length > 0 ? (
          <HubSpotNotificationInput notificationPrefix={notificationPrefix} hubSpotIntegrations={hubSpotIntegrations} />
        ) : notification.manualType === NotificationType.EMAIL ? (
          <EmailNotificationInput disableCustomize={disableCustomize} notificationPrefix={notificationPrefix} />
        ) : notification.manualType === NotificationType.LINEAR && linearInstances && linearInstances.length > 0 ? (
          <LinearNotificationInput
            disableCustomize={disableCustomize}
            notificationPrefix={notificationPrefix}
            linearInstances={linearInstances}
            mergeIntegrationData={mergeIntegrationData}
          />
        ) : notification.manualType === NotificationType.HEIGHT && heightAppInstances && heightAppInstances.length > 0 ? (
          <HeightAppNotificationInput
            disableCustomize={disableCustomize}
            notificationPrefix={notificationPrefix}
            heightAppInstances={heightAppInstances}
            mergeIntegrationData={mergeIntegrationData}
          />
        ) : notification.manualType === NotificationType.ASANA && asanaInstances && asanaInstances.length > 0 ? (
          <AsanaNotificationInput
            disableCustomize={disableCustomize}
            notificationPrefix={notificationPrefix}
            asanaInstances={asanaInstances}
            mergeIntegrationData={mergeIntegrationData}
          />
        ) : notification.manualType === NotificationType.JIRA && jiraInstances && jiraInstances.length > 0 ? (
          <JiraNotificationInput
            disableCustomize={disableCustomize}
            notificationPrefix={notificationPrefix}
            jiraInstances={jiraInstances}
            mergeIntegrationData={mergeIntegrationData}
          />
        ) : notification.manualType === NotificationType.TRELLO && trelloInstances && trelloInstances.length > 0 ? (
          <TrelloNotificationInput
            disableCustomize={disableCustomize}
            notificationPrefix={notificationPrefix}
            trelloInstances={trelloInstances}
            mergeIntegrationData={mergeIntegrationData}
          />
        ) : notification.manualType === NotificationType.FRONT && frontInstances && frontInstances.length > 0 ? (
          <FrontNotificationInput
            disableCustomize={disableCustomize}
            notificationPrefix={notificationPrefix}
            frontInstances={frontInstances}
            mergeIntegrationData={mergeIntegrationData}
          />
        ) : notification.manualType === NotificationType.INTERCOM && intercomIntegrations && intercomIntegrations.length > 0 ? (
          <IntercomNotificationInput notificationPrefix={notificationPrefix} intercomIntegrations={intercomIntegrations} />
        ) : null}
      </div>
      {notificationTypeToMappingType(notification.manualType) ? (
        <div>
          <div className="text-sm my-4">
            <SmartForm.Toggle
              disabled={
                notification.manualType === NotificationType.CUSTOMER_IO ||
                notification.manualType === NotificationType.INTERCOM ||
                notification.manualType === NotificationType.NOTION ||
                notification.manualType === NotificationType.SALESFORCE ||
                notification.manualType === NotificationType.HUBSPOT
              }
              name={`${notificationPrefix}.useMappingOverride`}
              title="Advanced Usage"
            />
          </div>
          {mappingFieldState.error && useMappingOverride ? (
            <div className="mt-4 text-red-500 text-xs whitespace-pre">{mappingFieldState.error.message ?? "One or more of these fields are invalid."}</div>
          ) : null}
          {useMappingOverride ? (
            <Controller
              control={control}
              rules={{
                required: true,
                validate: (mapping: ClientMapping) => isValidMapping({ mapping, notificationType: notification.manualType }),
              }}
              name={`${notificationPrefix}.mapping`}
              defaultValue={getTemplateForMappingType(notification.manualType)}
              render={({ field: { onChange, value } }) => (
                <div className="sm:col-span-6 mt-2">
                  <div className="flex w-full">
                    <Mapping
                      selectedIntegrationId={selectedIntegrationId ?? ""}
                      integrationData={mergeIntegrationData}
                      mapping={value}
                      onChange={onChange}
                      clientMappingApiType={notification.manualType}
                      prefix={`${notificationPrefix}.mapping`}
                      notificationPrefix={notificationPrefix}
                    />
                  </div>
                </div>
              )}
            />
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
